/** ***********
*
* <file-uploader.component.ts v1.0>
*
* Copyright (c) 2022 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*
* All rights reserved.
**************/

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, Optional, Output, Self, ViewChild } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NgControl } from '@angular/forms';
import { MatFormField, MatFormFieldControl } from '@angular/material/form-field';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { SnackbarComponent } from '../snackbar/snackbar';

@Component({
    selector: 'app-file-uploader',
    templateUrl: './file-uploader.component.html',
    styleUrls: ['./file-uploader.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: FileUploaderComponent,
        }
    ],
    standalone: false
})
export class FileUploaderComponent implements MatFormFieldControl<File[]>, ControlValueAccessor, AfterViewInit, OnDestroy {
  @Input()
  public get disabled(): boolean {
    return this._disabled;
  }
  public set disabled(isDisabled: boolean) {
    this._disabled = coerceBooleanProperty(isDisabled);
    if(this._disabled) this.fileForm.disable(); else this.fileForm.enable();
    this.stateChanges.next();
  }

  //#region MatFormFieldControl
  @Input()
  public get value(): File[] | null {
    return this._files;
  }
  public set value(files: File[] | null) {
    this._files = files;
    this.files$.next(this._files);
  }

  @Input()
  public get placeholder(): null {
    return null;
  }

  public get ngControl(): NgControl {
    return this._ngControl;
  }

  public get empty(): boolean {
    return !this._files || this._files.length === 0;
  }

  get shouldLabelFloat(): boolean {
    return false;
  }

  @Input()
  public get required(): boolean {
    return this._required;
  }
  public set required(required: boolean) {
    this._required = coerceBooleanProperty(required);
    this.stateChanges.next();
  }

  public get errorState(): boolean {
    return this.fileForm.invalid && this.touched;
  }

  constructor(
    @Optional()
    public parentFormField: MatFormField,
    @Self()
    @Optional()
    private _ngControl: NgControl,
    private _cd: ChangeDetectorRef,
    private _snackBar: SnackbarComponent,
    private _translate: TranslateService,
  ) {
    if (this._ngControl) {
      this._ngControl.valueAccessor = this;
    }
  }
  private static _nextId = 0;

  private _acceptRegexp = /(?<type>[\w*]*)[/.](?<format>[\w*]+)/;
  private _accept: string;
  public fileType: string;
  public fileFormats: string;
  @Input() set accept(value: string) {
    this._accept = value;
    const foundMatches = value.match(new RegExp(this._acceptRegexp, 'g')) ?? 0;
    if (foundMatches) {
        const matches = foundMatches.map(
            part => part.match(this._acceptRegexp).groups
        );
        const types = [...new Set(matches.map(({ type }) => type))].filter(v => v);
        this.fileType = types.includes('*') || !types.length ? this._translate.instant('FILE')?.toLowerCase() : types.join(', ');
        const formats = [...new Set(matches.map(({ format }) => format))].filter(v => v);
        this.fileFormats = formats.includes('*') || !formats.length ? this._translate.instant('ANY')?.toLowerCase() : formats.join(', ');
    } else {
        this.fileType = this._translate.instant('FILE')?.toLowerCase();
        this.fileFormats = this._translate.instant('ANY')?.toLowerCase();
    }
  }
  get accept(): string {
    return this._accept;
  }
  @Input() multiple = false;
  @Input() orientation: 'vertical'|'horizontal' = 'vertical';
  @Output() filesChange = new EventEmitter<File[]>();

  @HostBinding() public id = `file-uploader-${FileUploaderComponent._nextId}`;

  public stateChanges = new Subject<void>();

  public focused = false;

  public touched = false;

  public controlType = 'file-uploader';
  autofilled?: boolean;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-describedby') userAriaDescribedBy?: string;
  //#endregion

  public dropReady = false;
  public files$: Subject<File[]> = new Subject();
  public fileForm = new UntypedFormControl([]);
  public uniqueId = new Date().getTime();

  @ViewChild('fileDropRef') private _elementRef: ElementRef<HTMLInputElement>;
  private _files: File[] = [];
  private _required = false;
  private _disabled = false;
  public onFocusIn(): void {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }
  public onFocusOut(event: FocusEvent): void {
    if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }
  public setDescribedByIds(ids: string[]): void {
    const controlElement = this._elementRef.nativeElement;
    controlElement?.setAttribute('aria-describedby', ids.join(' '));
  }

  public onContainerClick(event: MouseEvent): void {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this._elementRef.nativeElement.querySelector('input').focus();
    }
  }

  ngAfterViewInit(): void {
    this.files$.pipe(
      distinctUntilChanged(),
    ).subscribe(
      async files => {
        this.filesChange.emit(files);
        this._onChange(files);
        await Promise.all(files.map(
          file => {
            if (this.isImage(file.type)) {
              this._drawImage(file);
            }
          }
        ));
        this.stateChanges.next();
      }
    );
    this.stateChanges.subscribe(
      () => {
        this._cd.markForCheck();
      }
    );
  }

  //#region Form
  writeValue(files: File[]): void {
    if(Array.isArray(files)) {
    this._files = [...files];
    }
  }

  registerOnChange(fn: (files: File[]) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  //#endregion

  setDropReadyStatus(status: boolean): void {
    this.dropReady = status;
    this.stateChanges.next();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private _onChange(files: File[]) {}
   
  public onTouched() {}

  public onFileSelected(event: Event): void {
    const fileList = (event.target as HTMLInputElement).files;
    this.dropReady = false;
    const files = [];
    for (let i = 0; i < fileList.length; i++) {
      files.push(fileList.item(i));
    }
    this._handleFiles(files);
    this.fileForm.patchValue(null);
  }

  private _handleFiles(files: File[]): void {
    files.forEach(
      file => {
        if (!this._checkType(file)) {
          this._snackBar.open(this._translate.instant('FILE_TYPE_INVALID', { type: file.type }), 'red-snackbar');
          return;
        }
        if (this.multiple) {
          const index = this._files.findIndex(_file => _file.name === file.name);
          if (index > -1) {
            this._files[index] = file;
          } else {
            this._files.push(file);
          }
        } else {
          this._files = [file];
        }
        this.files$.next(this._files);
      }
    );
  }

  private _checkType(file: File): boolean {
    const fileTypes = this.accept.match(new RegExp(this._acceptRegexp, 'g'));
    return fileTypes.some(
        acceptedType => new RegExp(acceptedType.replace(/\*/g, '.+')).test(file.type) || acceptedType.includes(file.name.replace(/^.*(\.\w+)$/, '$1'))
    );
  }

  public removeFile(removedFile: File): void {
    this._files = this._files.filter(
      file => file !== removedFile
    );
    this.files$.next(this._files);
  }

  public isImage(type: File['type']): boolean {
    return /^image\//.test(type);
  }

  public async getImageData(file: File): Promise<string> {
    return new Promise(
      (resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          resolve(reader.result as string);
          this._cd.markForCheck();
        };
        reader.onerror = (error) => reject(error);
      }
    );
  }

  private async _drawImage(file: File): Promise<void> {
    const dataUrl = await this.getImageData(file);
    const canvas = document.getElementById(`${this.uniqueId}-canvas-${file.name}`) as HTMLCanvasElement;
    const ctx = canvas?.getContext('2d');
    const img = new Image();
    return new Promise(
      (resolve, reject) => {
        img.onload = () => {
          let width: number, height: number;
          const ratioCanvas = canvas.width / canvas.height;
          const ratioImage = img.width / img.height;
          if (ratioCanvas > ratioImage) {
            height = canvas.height;
            width = img.width * (canvas.height / img.height);
          } else {
            width = canvas.width;
            height = img.height * (canvas.width / img.width);
          }
          ctx?.drawImage(img, 0, 0, width, height);
          resolve();
        };
        img.onerror = (error) => reject(error);
        img.src = dataUrl;
      }
    );
  }

  ngOnDestroy(): void {
    this.files$.unsubscribe();
    this.stateChanges.complete();
  }

}
